import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Server-side limit of `additionalContext` (POST generate / generate-sync). */
export const ADDITIONAL_CONTEXT_MAX_LENGTH = 50000;

/** One attached file, held ONLY in tab memory (never uploaded). */
export interface AdditionalContextFileState {
  /** Stable local key (files can share a name). */
  id: string;
  name: string;
  /** Extracted plain text; `null` while parsing or when parsing failed. */
  text: string | null;
  /** i18n key of the per-file error; errored files are NOT part of the context. */
  errorKey: string | null;
  parsing: boolean;
}

/** Free text + attached files of the "additional product information" block. */
export interface AdditionalContextState {
  text: string;
  files: AdditionalContextFileState[];
}

export const emptyAdditionalContextState = (): AdditionalContextState => ({
  text: '',
  files: [],
});

/**
 * Merge the block into the ONE string sent as `additionalContext`: the user's
 * free text first, then each successfully parsed file as
 * `--- Datei: <name> ---\n<text>` (header format fixed by the API contract).
 * Errored / still-parsing files are skipped - their state is shown in the UI.
 */
export const buildAdditionalContext = (state: AdditionalContextState): string => {
  const parts: string[] = [];
  if (state.text.trim().length > 0) {
    parts.push(state.text);
  }
  state.files.forEach((file) => {
    if (file.text != null) {
      parts.push(`--- Datei: ${file.name} ---\n${file.text}`);
    }
  });
  return parts.join('\n\n');
};

/** True while any attached file is still being parsed (blocks submitting). */
export const hasParsingAdditionalContextFiles = (state: AdditionalContextState): boolean =>
  state.files.some((f) => f.parsing);

/** Thrown for extensions we do not parse - mapped to the "unsupported" error. */
class UnsupportedFileFormatError extends Error {}

/**
 * Read ONE entry from a ZIP archive (enough for ODT: a ZIP with content.xml).
 * Hand-rolled instead of a zip dependency: locates the End-Of-Central-Directory
 * record, walks the central directory to the entry, and inflates it with the
 * browser-native DecompressionStream.
 * ponytail: no ZIP64 / encryption support - ODT documents never need either.
 */
const readZipEntry = async (
  buffer: ArrayBuffer,
  entryName: string
): Promise<Uint8Array | null> => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  // EOCD signature scan (record is 22 bytes + up to 64k comment).
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) {
    throw new Error('not a ZIP archive');
  }
  const entryCount = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder();
  for (let n = 0; n < entryCount; n += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) {
      break;
    }
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    if (name === entryName) {
      const localNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const dataStart = localHeaderOffset + 30 + localNameLength + localExtraLength;
      const data = bytes.subarray(dataStart, dataStart + compressedSize);
      if (method === 0) {
        return data;
      }
      if (method === 8) {
        const inflated = await new Response(
          new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
        ).arrayBuffer();
        return new Uint8Array(inflated);
      }
      throw new Error(`unsupported ZIP compression method ${method}`);
    }
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return null;
};

/**
 * Flatten ODF content.xml into plain text: paragraph/heading ends, line breaks
 * and tabs become whitespace, every other tag is stripped, XML entities are
 * decoded. Regex-based on purpose (works in any environment, no DOMParser).
 */
const odfXmlToText = (xml: string): string =>
  xml
    .replace(/<text:line-break[^>]*\/?>/g, '\n')
    .replace(/<text:tab[^>]*\/?>/g, '\t')
    .replace(/<\/text:[ph]>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&([a-z]+|#x?[0-9a-fA-F]+);/g, (whole, entity: string) => {
      switch (entity) {
        case 'amp':
          return '&';
        case 'lt':
          return '<';
        case 'gt':
          return '>';
        case 'quot':
          return '"';
        case 'apos':
          return "'";
        default:
          if (entity.startsWith('#x') || entity.startsWith('#X')) {
            return String.fromCodePoint(parseInt(entity.slice(2), 16));
          }
          if (entity.startsWith('#')) {
            return String.fromCodePoint(parseInt(entity.slice(1), 10));
          }
          return whole;
      }
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/**
 * Extract plain text from an attached file, fully on the CLIENT (the file is
 * never sent to the server). Parser libraries are loaded lazily via dynamic
 * `import()` so they stay out of the main bundle.
 */
const parseFileText = async (file: File): Promise<string> => {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  if (ext === 'txt' || ext === 'csv') {
    return file.text();
  }
  if (ext === 'odt') {
    // ODT = ZIP with the document text in content.xml.
    const content = await readZipEntry(await file.arrayBuffer(), 'content.xml');
    if (!content) {
      throw new Error('content.xml not found in ODT');
    }
    return odfXmlToText(new TextDecoder().decode(content));
  }
  if (ext === 'pdf') {
    const pdfjs = await import('pdfjs-dist');
    // Run pdf.js on the MAIN thread: pre-loading the worker module as
    // `globalThis.pdfjsWorker` makes PDFWorker._initialize() take the fake-
    // worker path immediately - no worker asset is fetched at runtime, so
    // parsing works regardless of how the dev/prod server serves .mjs files
    // (a runtime worker fetch is exactly what silently failed before).
    // ponytail: blocks the main thread while parsing; fine for these small
    // uploads - switch back to a real worker if huge PDFs ever become a case.
    if (!(globalThis as { pdfjsWorker?: unknown }).pdfjsWorker) {
      (globalThis as { pdfjsWorker?: unknown }).pdfjsWorker =
        // @ts-expect-error - the worker subpath ships no type declarations.
        await import('pdfjs-dist/build/pdf.worker.min.mjs');
    }
    // isEvalSupported: false - hardening against CVE-2024-4367-style JS
    // execution from crafted PDFs (fixed in >=4.2.67, disabled here anyway).
    const doc = await pdfjs.getDocument({
      data: await file.arrayBuffer(),
      isEvalSupported: false,
    }).promise;
    try {
      const pages: string[] = [];
      for (let i = 1; i <= doc.numPages; i += 1) {
        const page = await doc.getPage(i);
        const content = await page.getTextContent();
        pages.push(
          content.items
            .map((item: unknown) => {
              const str = (item as { str?: unknown }).str;
              return typeof str === 'string' ? str : '';
            })
            .join(' ')
        );
      }
      return pages.join('\n');
    } finally {
      void doc.destroy();
    }
  }
  if (ext === 'docx') {
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
    return result.value;
  }
  if (ext === 'xlsx') {
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    return workbook.SheetNames.map((name: string) =>
      XLSX.utils.sheet_to_csv(workbook.Sheets[name])
    ).join('\n');
  }
  throw new UnsupportedFileFormatError(ext);
};

export interface AdditionalContextInputProps {
  /**
   * Snapshot to start from. The component owns its state while mounted (file
   * parsing is async) and reports every change through `onStateChange`; the
   * parent keeps the latest snapshot so the block survives closing/reopening
   * the modal it lives in (the context must stay available for a re-generate).
   */
  initialValue: AdditionalContextState;
  onStateChange: (state: AdditionalContextState) => void;
  disabled?: boolean;
}

/**
 * "Additional product information" block of the SINGLE generation forms
 * (Content Master generate modal + marketplace-listing single confirm modal):
 * a free-text textarea plus attached files parsed to plain text on the client
 * (.txt / .csv / .pdf / .docx / .xlsx / .odt). Everything lives in tab memory only -
 * nothing is uploaded; the merged string is sent as `additionalContext` of ONE
 * generate request and is never stored. Shows the combined length against the
 * 50 000 limit; the PARENT blocks submitting while over the limit or while a
 * file is still parsing (`buildAdditionalContext` /
 * `hasParsingAdditionalContextFiles`).
 */
const AdditionalContextInput: React.FC<AdditionalContextInputProps> = ({
  initialValue,
  onStateChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<AdditionalContextState>(initialValue);
  const idSeq = useRef(0);

  // Report upward via a ref so the effect never re-fires on a new callback
  // identity - only on actual state changes.
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  useEffect(() => {
    onStateChangeRef.current(state);
  }, [state]);

  const addFile = async (file: File, id: string) => {
    setState((prev) => ({
      ...prev,
      files: [
        ...prev.files,
        { id, name: file.name, text: null, errorKey: null, parsing: true },
      ],
    }));
    try {
      const text = await parseFileText(file);
      setState((prev) => ({
        ...prev,
        files: prev.files.map((f) => (f.id === id ? { ...f, text, parsing: false } : f)),
      }));
    } catch (err) {
      // Surface the real reason in the console - the UI shows a generic
      // message, which makes parser failures otherwise undiagnosable.
      // eslint-disable-next-line no-console
      console.warn(`additional-context: failed to parse "${file.name}"`, err);
      const errorKey =
        err instanceof UnsupportedFileFormatError
          ? 'cm_additional_context_file_unsupported'
          : 'cm_additional_context_file_parse_failed';
      setState((prev) => ({
        ...prev,
        files: prev.files.map((f) => (f.id === id ? { ...f, errorKey, parsing: false } : f)),
      }));
    }
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    // Reset the input so the same file can be re-attached after removal.
    event.target.value = '';
    selected.forEach((file) => {
      idSeq.current += 1;
      void addFile(file, `ctx-file-${idSeq.current}-${Date.now()}`);
    });
  };

  const removeFile = (id: string) => {
    setState((prev) => ({ ...prev, files: prev.files.filter((f) => f.id !== id) }));
  };

  const combined = buildAdditionalContext(state);
  const tooLong = combined.length > ADDITIONAL_CONTEXT_MAX_LENGTH;
  // No file-count limit: everything is parsed locally, the 50k character
  // budget is the only real constraint.
  const attachDisabled = disabled;

  return (
    <div className="additional-context">
      <span className="additional-context__title">{t('cm_additional_context_title')}</span>
      <textarea
        className="additional-context__textarea"
        value={state.text}
        onChange={(e) => {
          const text = e.target.value;
          setState((prev) => ({ ...prev, text }));
        }}
        placeholder={t('cm_additional_context_placeholder')}
        rows={4}
        disabled={disabled}
      />

      {state.files.length > 0 ? (
        <ul className="additional-context__files">
          {state.files.map((file) => (
            <li key={file.id} className="additional-context__file">
              <span className="additional-context__file-name" title={file.name}>
                {file.name}
              </span>
              {file.parsing ? (
                <span className="additional-context__file-status">
                  {t('cm_additional_context_file_parsing')}
                </span>
              ) : null}
              {file.errorKey ? (
                <span className="additional-context__file-status additional-context__file-status--error">
                  {t(file.errorKey)}
                </span>
              ) : null}
              <button
                type="button"
                className="additional-context__file-remove"
                onClick={() => removeFile(file.id)}
                disabled={disabled}
                aria-label={t('cm_additional_context_file_remove')}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="additional-context__toolbar">
        <label
          className={`content-master-link-button additional-context__attach${
            attachDisabled ? ' additional-context__attach--disabled' : ''
          }`}
        >
          <input
            type="file"
            accept=".txt,.csv,.pdf,.docx,.xlsx,.odt"
            multiple
            onChange={handleFilesSelected}
            disabled={attachDisabled}
            className="additional-context__attach-input"
          />
          {t('cm_additional_context_attach')}
        </label>
        <span
          className={`additional-context__counter${
            tooLong ? ' additional-context__counter--over' : ''
          }`}
          aria-live="polite"
        >
          {combined.length.toLocaleString()} / {ADDITIONAL_CONTEXT_MAX_LENGTH.toLocaleString()}
        </span>
      </div>

      {tooLong ? (
        <div className="additional-context__error">
          {t('cm_additional_context_too_long')}
        </div>
      ) : null}

      {/* Always visible: extra context is re-sent with every generation chunk
          and noticeably raises the token cost (surcharge shown by the parent). */}
      <div className="additional-context__token-warning">
        {t('cm_additional_context_token_warning')}
      </div>
      <div className="additional-context__hint">{t('cm_additional_context_hint')}</div>
    </div>
  );
};

export default AdditionalContextInput;
